import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCourts, useFederalStates } from '../Aemter/hooks';
import { useGetLandRegisterAccesses } from '../hooks/reactQuerys/FederalStateLoginQueries';
import { useTriggerLandRegisterDownload } from '../hooks/reactQuerys/LandMarkingQueries';
import { searchOptionLandRegisterSchema } from '../Interfaces/Helper/ISearchOptionLandRegister';
import { useAlert } from '../context/AlertContext';

const DOC_TYPES: { key: string; label: string }[] = [
  { key: 'GB', label: 'Grundbuchauszug' },
  { key: 'AN', label: 'Aktualitätsnachweis' },
  { key: 'MT', label: 'Markentabelle' },
];

interface IProps {
  open: boolean;
  onClose: () => void;
  /** Blatt, für das abgerufen wird (Court/Gemarkung/Blattnummer kommen serverseitig daher). */
  idGrundbuchblatt: number;
  blattnummer?: string;
  /** Amtsgericht des Blatts — nur zum Vorbelegen des Bundeslands. */
  idCourt?: number | null;
  /** Nach erfolgreichem Abruf (Abrufe-Liste neu laden). */
  onDone?: () => void;
}

/**
 * Grundbuch-Abruf direkt am Blatt (Grundbuchauszug/Aktualitätsnachweis/
 * Markentabelle). Sendet ``idGrundbuchblatt`` an ``/landregister/download`` — das
 * Backend leitet Amtsgericht/Gemarkung/Blattnummer aus dem Blatt ab und speichert
 * den Abruf an ``GrundbuchblattAbruf``. Passwort wird nur für den Abruf verwendet.
 */
export const BlattAbrufDialog: React.FC<IProps> = ({
  open,
  onClose,
  idGrundbuchblatt,
  blattnummer,
  idCourt,
  onDone,
}) => {
  const { showAlert } = useAlert();
  const { federalStates } = useFederalStates();
  const { courts } = useCourts();
  const { landRegisterAccesses } = useGetLandRegisterAccesses();
  const { downloadLandRegister, isDownloadingLandMarkings } =
    useTriggerLandRegisterDownload();

  const courtFederalState = useMemo(
    () => courts?.find(c => c.idCourt === idCourt)?.idFederalState ?? null,
    [courts, idCourt]
  );

  const [idFederalState, setIdFederalState] = useState<number | null>(null);
  const [idFederalStateLogin, setIdFederalStateLogin] = useState<number | null>(
    null
  );
  const [selected, setSelected] = useState<string[]>(['GB']);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState('');

  useEffect(() => {
    if (!open) return;
    setIdFederalState(courtFederalState);
    setSelected(['GB']);
    setPw('');
    setPwOpen(false);
    const acc = (landRegisterAccesses ?? []).filter(
      a => a.isFirstStage === false && a.idFederalState === courtFederalState
    );
    setIdFederalStateLogin(
      acc.length === 1 ? acc[0].idFederalStateLogin : null
    );
  }, [open, courtFederalState, landRegisterAccesses]);

  const accesses = (landRegisterAccesses ?? []).filter(
    a => a.isFirstStage === false && a.idFederalState === idFederalState
  );

  const toggle = (k: string) =>
    setSelected(prev =>
      prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]
    );

  const canStart = !!idFederalStateLogin && selected.length > 0;

  const runAbruf = async (password: string) => {
    setPwOpen(false);
    try {
      const body = searchOptionLandRegisterSchema.parse({
        idGrundbuchblatt,
        idFederalState,
        idFederalStateLogin,
        SelectedDownload: selected,
        Password: password,
      });
      await downloadLandRegister(body);
      showAlert({
        severity: 'success',
        text: 'Abruf erfolgreich durchgeführt.',
      });
      onDone?.();
      onClose();
    } catch {
      showAlert({
        severity: 'error',
        text: 'Der Abruf konnte nicht durchgeführt werden. Bitte Zugang, Passwort und die Verfügbarkeit des Grundbuchs prüfen.',
      });
    }
  };

  return (
    <>
      {isDownloadingLandMarkings && (
        <Dialog open>
          <DialogContent>
            <Typography>
              Das Grundbuch wird abgerufen. <br />
              Dies kann mehrere Minuten dauern, bitte haben Sie Geduld!
            </Typography>
            <CircularProgress sx={{ mt: 1 }} />
          </DialogContent>
        </Dialog>
      )}

      {/* Passwort erst beim Beziehen abfragen (nicht gespeichert) */}
      <Dialog
        open={pwOpen}
        onClose={() => setPwOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Passwort des Zugangs</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            Wird nur für diesen Abruf verwendet und nicht gespeichert.
          </Typography>
          <TextField
            type='password'
            label='Passwort'
            fullWidth
            autoFocus
            value={pw}
            onChange={e => setPw(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && pw) void runAbruf(pw);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setPwOpen(false)}>
            Abbruch
          </Button>
          <Button
            variant='contained'
            disabled={!pw}
            onClick={() => void runAbruf(pw)}
          >
            Abruf starten
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
        <DialogTitle>
          Grundbuch-Abruf{blattnummer ? ` – Blatt ${blattnummer}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              select
              label='Bundesland'
              size='small'
              value={idFederalState ?? ''}
              onChange={e => {
                const v = e.target.value ? Number(e.target.value) : null;
                setIdFederalState(v);
                setIdFederalStateLogin(null);
              }}
            >
              {(federalStates ?? [])
                .filter(f => f.isEnabledSolumWeb)
                .map(f => (
                  <MenuItem key={f.idFederalState} value={f.idFederalState}>
                    {f.FederalState}
                  </MenuItem>
                ))}
            </TextField>

            <TextField
              select
              label='Zugang'
              size='small'
              disabled={!idFederalState}
              value={idFederalStateLogin ?? ''}
              onChange={e =>
                setIdFederalStateLogin(
                  e.target.value ? Number(e.target.value) : null
                )
              }
            >
              {accesses.map(a => (
                <MenuItem
                  key={a.idFederalStateLogin}
                  value={a.idFederalStateLogin}
                >
                  {a.Username}
                </MenuItem>
              ))}
            </TextField>

            <Stack>
              <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
                Abrufe
              </Typography>
              {DOC_TYPES.map(d => (
                <FormControlLabel
                  key={d.key}
                  control={
                    <Checkbox
                      size='small'
                      checked={selected.includes(d.key)}
                      onChange={() => toggle(d.key)}
                    />
                  }
                  label={d.label}
                />
              ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            disabled={!canStart}
            onClick={() => setPwOpen(true)}
          >
            Abruf beziehen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
